 
import { Formik, FormikHelpers } from 'formik';
import { withZodSchema } from 'formik-validator-zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { postAssetInvestmentDataUpdate } from 'src/api/stoApi';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { AssetFormActionsContainer } from 'src/components/assets/form-components/AssetFormActionsContainer';
import { AssetFormError } from 'src/components/assets/form-components/AssetFormError';
import { InvestmentInformation } from 'src/components/assets/investment-forms/InvestmentInformation';
import { InvestmentTerms } from 'src/components/assets/investment-forms/InvestmentTerms';
import { IssuePriceCalculation } from 'src/components/assets/investment-forms/IssuePriceCalculation';
import { Issuer } from 'src/components/assets/investment-forms/Issuer';
import { IssuingDetails } from 'src/components/assets/investment-forms/IssuingDetails';
import { PurchasePriceCalculation } from 'src/components/assets/investment-forms/PurchasePriceCalculation';
import { SecurityInformation } from 'src/components/assets/investment-forms/SecurityInformation';
import { SecurityTypeDetails } from 'src/components/assets/investment-forms/SecurityTypeDetails';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { TwoColumnGridContainer } from 'src/components/ui/TwoColumnGridContainer';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { generalStore } from 'src/stores/GeneralStore';
import { isNullUndefinedOrEmpty, removeNullUndefined } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Alert, Button, Snackbar } from '@mui/material';
import { useAsset } from '../../context-providers/AssetContext';
import { assetInvestmentDetailsSchema, TAssetInvestmentDetailsSchema } from '@brickwise/asset-validation/dist/asset/investmentData';
import { AxiosError } from 'axios';
import { getInitFormValues } from 'src/util/formUtils';
import { ReturnOnEquityCalculation } from 'src/components/assets/investment-forms/ReturnOnEquityCalculation';
import { LeverageInformation } from 'src/components/assets/investment-forms/LeverageInformation';
import { CashFlow } from 'src/components/assets/investment-forms/CashFlow';
import { AssetTypeConditional } from '../../components/user/AssetTypeConditional';
import { PaymentDetails } from '../../components/assets/investment-forms/PaymentDetails';

export const AssetInvestmentPage = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const { asset, isEditable } = useAsset();
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!asset) {
    navigate(Routes.ASSETS.EDIT.replace(':assetId', assetId ?? ''));
  }

  const initialErrors = withZodSchema(assetInvestmentDetailsSchema)(asset);

  const saveClicked = (formikHelpers: FormikHelpers<any>) => async (assetInvestmentPayload: TAssetInvestmentDetailsSchema) => {
    // put asset
    generalStore.setIsLoading(true);
    try {
      await postAssetInvestmentDataUpdate(asset?.id ?? '', assetInvestmentPayload);
      setOpenSuccessAlert(true);
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.response?.data?.errorMapping) {
          formikHelpers.setErrors(error.response.data.errorMapping);
        }
      }
      console.log(error);
      generalStore.setError('Error while update asset');
    } finally {
      generalStore.setIsLoading(false);
    }
  };
  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: (isNullUndefinedOrEmpty(asset?.descriptions?.title) ? asset?.id : asset?.descriptions?.title) ?? '',
          },
          {
            link: Routes.ASSETS.INVESTMENT.replace(':assetId', asset?.id ?? ''),
            label: t('dashboard.breadcrumbs.investment'),
          },
        ]}
        headline={isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions.title as string)}
      >
        <AssetDetailsLinkList
          assetId={asset.id}
          useSerialNumber={asset.useSerialNumber}
          active={'investment'}
          isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
          isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
          isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
          securityType={asset?.investmentDetails?.securityType}
        />
      </SiteHeader>
      {!isEditable && (
        <Alert sx={{ marginBottom: '24px' }} severity="warning">
          {t('dashboard.assets.addedit.notEditable')}
        </Alert>
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setOpenSuccessAlert(false)}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('dashboard.assets.addedit.successalert')}
        </Alert>
      </Snackbar>
      <Formik
        initialTouched={initialErrors}
        initialErrors={initialErrors}
        initialValues={getInitFormValues(assetInvestmentDetailsSchema, asset)}
        validate={(values) => {
          const res = withZodSchema(assetInvestmentDetailsSchema)(values);
          console.log(res);
          return res;
        }}
        onSubmit={async (values, formikHelpers) => {
          const parseResult = assetInvestmentDetailsSchema.safeParse(values);
          if (parseResult.success === true) {
            const packagedAsset = removeNullUndefined(parseResult.data);
            await saveClicked(formikHelpers)(packagedAsset);
          }
        }}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit}>
            <TwoColumnGridContainer>
              <PurchasePriceCalculation />
              <IssuePriceCalculation disabled={!isEditable} />
            </TwoColumnGridContainer>
            <IssuingDetails disabled={!isEditable} />
            <SecurityTypeDetails disabled={!isEditable} />
            <AssetTypeConditional supported={['builderOwnerModel', 'powerplantPortfolio']}>
              <PaymentDetails />
            </AssetTypeConditional>
            <AssetTypeConditional supported={['realEstate']}>
              <ReturnOnEquityCalculation disabled={false} />
            </AssetTypeConditional>
            <AssetTypeConditional supported={['realEstate']}>
              <LeverageInformation disabled={false} />
            </AssetTypeConditional>
            <AssetTypeConditional supported={['realEstate']}>
              <CashFlow disabled={false} />
            </AssetTypeConditional>
            <TwoColumnGridContainer>
              <InvestmentInformation disabled={!isEditable} />
              <SecurityInformation disabled={!isEditable} />
            </TwoColumnGridContainer>
            <TwoColumnGridContainer>
              <Issuer disabled={!isEditable} />
              <InvestmentTerms disabled={!isEditable} />
            </TwoColumnGridContainer>
            <AssetFormError />
            <AssetFormActionsContainer>
              <Button
                variant="contained"
                color="inherit"
                disableElevation
                onClick={() => navigate(Routes.ASSETS.EDIT.replace(':assetId', assetId ?? ''))}
              >
                {t('dashboard.assets.addedit.investment.back')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                disabled={false} // we want to let users update asset investment even after emission
                type="submit"
                data-cy="asset-investment-save-button"
              >
                {t('dashboard.assets.addedit.investment.save')}
              </Button>
            </AssetFormActionsContainer>
          </form>
        )}
      </Formik>
    </>
  );
};
